// DateTimeRangePicker.jsx
import React from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";

const pickerSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: "Host Grotesk, sans-serif",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    transition: "all 0.2s ease",
    "&:hover fieldset": {
      borderColor: "#0f766e",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0f766e",
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Host Grotesk, sans-serif",
    color: "#64748b",
    fontWeight: 500,
    "&.Mui-focused": {
      color: "#0f766e",
    },
  },
  "& .MuiInputBase-input": {
    fontFamily: "Host Grotesk, sans-serif",
    fontSize: "0.875rem",
    color: "#334155",
  },
  "& .MuiIconButton-root": {
    color: "#0f766e",
    "&:hover": {
      backgroundColor: "rgba(15, 118, 110, 0.08)",
    },
  },
};

const pickerPopperSx = {
  "& .MuiPaper-root": {
    borderRadius: "12px",
    boxShadow: "0 12px 40px rgba(15, 118, 110, 0.15)",
    border: "1px solid rgba(15, 118, 110, 0.1)",
    fontFamily: "Host Grotesk, sans-serif",
  },
  "& .MuiPickersCalendarHeader-root": {
    fontFamily: "Host Grotesk, sans-serif",
  },
  "& .MuiPickersDay-root": {
    fontFamily: "Host Grotesk, sans-serif",
    "&.Mui-selected": {
      backgroundColor: "#0f766e",
      "&:hover": {
        backgroundColor: "#0d5b57",
      },
    },
    "&:hover": {
      backgroundColor: "rgba(15, 118, 110, 0.1)",
    },
  },
  "& .MuiPickersYear-yearButton": {
    fontFamily: "Host Grotesk, sans-serif",
    "&.Mui-selected": {
      backgroundColor: "#0f766e",
      "&:hover": {
        backgroundColor: "#0d5b57",
      },
    },
  },
  "& .MuiMultiSectionDigitalClock-root": {
    fontFamily: "Host Grotesk, sans-serif",
  },
};

const DateTimeRangePicker = ({
  fromDateTime,
  setFromDateTime,
  toDateTime,
  setToDateTime,
  minDate,
  maxDate,
  onClear,
}) => {
  const handleFromChange = (newValue) => {
    setFromDateTime(newValue ? dayjs(newValue).format("YYYY-MM-DDTHH:mm:ss") : "");
  };

  const handleToChange = (newValue) => {
    setToDateTime(newValue ? dayjs(newValue).format("YYYY-MM-DDTHH:mm:ss") : "");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: "600",
              color: "#334155",
              fontFamily: "Host Grotesk, sans-serif",
            }}
          >
            Custom Date &amp; Time
          </Typography>
          {(fromDateTime || toDateTime) && onClear && (
            <Tooltip title="Clear dates">
              <IconButton
                size="small"
                onClick={onClear}
                sx={{ color: "#94a3b8", "&:hover": { color: "#0f766e" } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <DateTimePicker
            label="From"
            value={fromDateTime ? dayjs(fromDateTime) : null}
            onChange={handleFromChange}
            format="DD-MM-YYYY HH:mm"
            minDateTime={minDate}
            maxDateTime={maxDate}
            slotProps={{
              textField: { size: "small", fullWidth: true, sx: pickerSx },
              popper: { sx: pickerPopperSx },
            }}
          />
          <DateTimePicker
            label="To"
            value={toDateTime ? dayjs(toDateTime) : null}
            onChange={handleToChange}
            format="DD-MM-YYYY HH:mm"
            minDateTime={minDate}
            maxDateTime={maxDate}
            slotProps={{
              textField: { size: "small", fullWidth: true, sx: pickerSx },
              popper: { sx: pickerPopperSx },
            }}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DateTimeRangePicker;
